import { useEffect, useRef, useState } from "react";
import { auth } from "../api/client";
import { bboxParam, type Aircraft, type Bbox } from "./types";

export type LiveState = {
  aircraft: Aircraft[];
  /** Wall-clock ms when the current snapshot arrived — lets the UI keep ageing between ticks. */
  receivedAt: number;
  connected: boolean;
  /**
   * True while the current subscription has not delivered its first snapshot. Derived from which
   * subscription each snapshot answered rather than toggled per event, so it changes only when the
   * viewport moves to a new area — never flickers on the regular 5-second ticks.
   */
  loading: boolean;
};

/**
 * Subscribe to the server's view of the sky for a bbox.
 *
 * EventSource reconnects on its own, which is most of why this is SSE and not a WebSocket. The
 * one thing it cannot do is tell us *why* it failed — a 401 from an expired session looks
 * exactly like a dropped connection, and it will retry that forever. So on error we ask
 * /auth/status once, and hand the answer up rather than silently reconnecting into a wall.
 */
export function useLiveAircraft(bbox: Bbox | null, onUnauthenticated: () => void): LiveState {
  const [state, setState] = useState<Omit<LiveState, "loading">>({ aircraft: [], receivedAt: Date.now(), connected: false });
  const [snapshotKey, setSnapshotKey] = useState<string | null>(null);
  // Held in a ref so a changing callback identity never tears down the stream.
  const onUnauth = useRef(onUnauthenticated);
  onUnauth.current = onUnauthenticated;

  const key = bbox ? bboxParam(bbox) : null;

  useEffect(() => {
    if (!key) return;

    const source = new EventSource(`/api/stream?bbox=${key}`);
    let closed = false;

    source.addEventListener("open", () => {
      setState((s) => ({ ...s, connected: true }));
    });

    source.addEventListener("aircraft", (event) => {
      const payload = JSON.parse((event as MessageEvent).data) as { aircraft: Aircraft[] };
      setState({ aircraft: payload.aircraft, receivedAt: Date.now(), connected: true });
      setSnapshotKey(key);
    });

    source.addEventListener("error", () => {
      if (closed) return;
      setState((s) => ({ ...s, connected: false }));
      auth
        .status()
        .then((s) => {
          if (!s.authenticated) {
            source.close();
            onUnauth.current();
          }
        })
        .catch(() => {
          // Genuinely offline. EventSource is already retrying; nothing useful to do here.
        });
    });

    return () => {
      closed = true;
      source.close();
    };
  }, [key]);

  return { ...state, loading: key !== null && snapshotKey !== key };
}

/** A counter that ticks every `ms`, to re-render time-dependent UI (marker fade, "12s ago"). */
export function useTicker(ms: number): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), ms);
    return () => clearInterval(id);
  }, [ms]);
  return tick;
}

/** `value`, but only once it has stayed true for `delayMs` — so a state that resolves almost
 *  immediately (the stream sends a snapshot the moment it connects) never flashes a spinner. */
export function useDelayedFlag(value: boolean, delayMs = 300): boolean {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!value) {
      setShown(false);
      return;
    }
    const timer = setTimeout(() => setShown(true), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return shown;
}
